import React, { useCallback, useEffect, useState } from "react";
import { Modal, Spin, Tag, Tooltip } from "antd";
import { useDispatch, useSelector } from "react-redux";

import CustomFieldEditor from "../components/CustomFieldEditor";
import * as projectsApi from "../../../../core/data/redux/services/projectsApi";
import { fetchCustomFields } from "../../../../core/data/redux/projects/thunks";
import {
  selectActiveCompany,
  selectCurrentProject,
  selectCustomFields,
} from "../../../../core/data/redux/projects/selectors";
import { useProjectCapability } from "../../hooks/useProjectRole";
import { errMsg } from "../../helpers/errMsg.helper";
import { CONFIRM_MODAL_PROPS } from "../../helpers/modal.style";
import { notify } from "../../../../services/toast.config";

import type {
  CustomField,
  CustomFieldAppliesTo,
  CustomFieldType,
} from "../../../../core/data/redux/projects/types";
import type { AppDispatch } from "../../../../core/data/redux/store";

const CustomFieldsSettings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const project = useSelector(selectCurrentProject);
  const customFields = useSelector(selectCustomFields);
  const activeCompany = useSelector(selectActiveCompany);
  const canManage = useProjectCapability(project?.id, "MANAGE_CUSTOM_FIELD");

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingField, setEditingField] = useState<CustomField | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      await dispatch(fetchCustomFields(project.id)).unwrap();
    } catch {
      /* silently handled */
    } finally {
      setLoading(false);
    }
  }, [dispatch, project?.id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleCreate = useCallback(() => {
    setEditingField(null);
    setEditorOpen(true);
  }, []);

  const handleEdit = useCallback((field: CustomField) => {
    setEditingField(field);
    setEditorOpen(true);
  }, []);

  const handleSubmit = useCallback(
    async (payload: {
      name: string;
      field_type: CustomFieldType;
      applies_to: CustomFieldAppliesTo;
      options: any[] | null;
    }) => {
      if (!project) return;
      setSubmitting(true);
      try {
        if (editingField) {
          await projectsApi.updateCustomField(
            activeCompany.company_id,
            activeCompany.company_type,
            editingField.id,
            payload
          );
          notify.success("Custom field updated");
        } else {
          await projectsApi.createCustomField(
            activeCompany.company_id,
            activeCompany.company_type,
            { project_id: project.id, ...payload }
          );
          notify.success("Custom field created");
        }
        await reload();
        setEditorOpen(false);
        setEditingField(null);
      } catch (e: any) {
        notify.error(errMsg(e, "Failed to save custom field"));
        throw e;
      } finally {
        setSubmitting(false);
      }
    },
    [activeCompany, editingField, project, reload]
  );

  const handleDelete = useCallback(
    (field: CustomField) => {
      Modal.confirm({
        title: `Delete custom field '${field.name}'?`,
        content:
          "All values stored on issues for this field will be removed. This cannot be undone.",
        okText: "Delete",
        okButtonProps: { danger: true },
        ...CONFIRM_MODAL_PROPS,
        onOk: async () => {
          try {
            await projectsApi.deleteCustomField(
              activeCompany.company_id,
              activeCompany.company_type,
              field.id
            );
            await reload();
            notify.success("Custom field deleted");
          } catch (e: any) {
            notify.error(errMsg(e, "Failed to delete custom field"));
          }
        },
      });
    },
    [activeCompany, reload]
  );

  if (!project) {
    return (
      <div className="card border-0" style={{ borderRadius: 14, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
        <div className="card-body d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status" style={{ width: "2.5rem", height: "2.5rem" }} />
            <p className="mt-3 text-muted mb-0">Loading project…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card border-0" style={{ borderRadius: 14, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
      <div className="card-header border-0 pb-0 d-flex align-items-center justify-content-between">
        <div>
          <h5 className="mb-1 d-flex align-items-center gap-2" style={{ whiteSpace: "nowrap" }}>
            <i className="ti ti-forms" style={{ color: "#3b82f6", fontSize: 20, flexShrink: 0 }} />
            <span>Custom Fields</span>
          </h5>
          <span className="text-muted small">
            Add project-specific data fields to issues.
          </span>
        </div>
        {canManage && (
          <button type="button" className="btn btn-info btn-sm d-inline-flex align-items-center gap-1" style={{ borderRadius: 8, whiteSpace: "nowrap" }} onClick={handleCreate}>
            <i className="ti ti-plus" style={{ fontSize: 16 }} />
            <span>New Field</span>
          </button>
        )}
      </div>
      <div className="card-body bg-white rounded-3">
        {loading && customFields.length === 0 ? (
          <div className="text-center py-3">
            <Spin />
          </div>
        ) : customFields.length === 0 ? (
          <div className="text-center py-4">
            <i className="ti ti-forms" style={{ fontSize: 40, color: "#cbd5e1" }} />
            <p className="text-muted mt-2 mb-0">No custom fields yet. Create one to capture additional issue data.</p>
          </div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {customFields.map((field) => (
              <div
                key={field.id}
                className="d-flex align-items-center gap-3 px-3 py-2"
                style={{ borderRadius: 10, border: "1px solid #e2e8f0", transition: "all 0.2s" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#f8fafc"; e.currentTarget.style.borderColor = "#93c5fd"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "#e2e8f0"; }}
              >
                <div className="d-flex align-items-center justify-content-center" style={{ width: 32, height: 32, borderRadius: 8, background: "#eff6ff", flexShrink: 0 }}>
                  <i className="ti ti-forms" style={{ color: "#3b82f6", fontSize: 16 }} />
                </div>
                <div className="flex-grow-1 min-width-0">
                  <div className="fw-semibold" style={{ fontSize: 13.5 }}>{field.name}</div>
                  <div className="d-flex flex-wrap align-items-center gap-1 mt-1">
                    <Tag style={{ margin: 0, fontSize: 10, lineHeight: "18px", borderRadius: 10 }}>{field.field_type}</Tag>
                    <Tag color="default" style={{ margin: 0, fontSize: 10, lineHeight: "18px", borderRadius: 10 }}>Applies to: {field.applies_to}</Tag>
                    {Array.isArray(field.options) && field.options.length > 0 && (
                      <span className="text-muted" style={{ fontSize: 11 }}>{field.options.length} option{field.options.length === 1 ? "" : "s"}</span>
                    )}
                  </div>
                </div>
                {canManage && (
                  <div className="d-flex align-items-center gap-1" style={{ flexShrink: 0 }}>
                    <Tooltip title="Edit"><button type="button" className="btn btn-sm btn-light d-flex align-items-center justify-content-center" style={{ borderRadius: 8, width: 32, height: 32 }} onClick={() => handleEdit(field)}><i className="ti ti-edit" style={{ color: "#f59e0b", fontSize: 15 }} /></button></Tooltip>
                    <Tooltip title="Delete"><button type="button" className="btn btn-sm btn-light d-flex align-items-center justify-content-center" style={{ borderRadius: 8, width: 32, height: 32 }} onClick={() => handleDelete(field)}><i className="ti ti-trash" style={{ color: "#ef4444", fontSize: 15 }} /></button></Tooltip>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <CustomFieldEditor
        open={editorOpen}
        initialField={editingField}
        submitting={submitting}
        onSubmit={handleSubmit}
        onCancel={() => {
          setEditorOpen(false);
          setEditingField(null);
        }}
      />
    </div>
  );
};

export default CustomFieldsSettings;
